'use client';

import NextLink from 'next/link';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { DEEP_LINK_UNAVAILABLE_EXPLANATION } from '@/lib/auth/resourceDeepLink';

export type ResourceUnavailableViewProps = {
  /** Primary heading */
  headline: string;
  /** Shown below the explanatory paragraph (e.g. identifier from the link) */
  detail?: string;
  /** Body copy; defaults to vague deep-link explanation (missing vs forbidden is unknown). */
  explanation?: string;
  /** Path after basePath (e.g. `/shippings`, `/samples`) */
  searchPath: string;
  /** Button label */
  searchLabel: string;
};

/**
 * Centered message when a deep-linked entity cannot be shown, with navigation to the relevant search/list page.
 */
export function ResourceUnavailableView({
  headline,
  detail,
  explanation = DEEP_LINK_UNAVAILABLE_EXPLANATION,
  searchPath,
  searchLabel,
}: ResourceUnavailableViewProps) {
  const systemConfig = useSystemConfig();
  const base = systemConfig?.basePath ?? '';
  const href = `${base}${searchPath.startsWith('/') ? searchPath : `/${searchPath}`}`;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        p: 3,
      }}
    >
      <Stack spacing={2} sx={{ maxWidth: 440, textAlign: 'center', alignItems: 'center' }}>
        <Typography variant="h5" component="h1" fontWeight={600}>
          {headline}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {explanation}
        </Typography>
        {detail ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {detail}
          </Typography>
        ) : null}
        <Button component={NextLink} href={href} variant="contained" size="large">
          {searchLabel}
        </Button>
      </Stack>
    </Box>
  );
}
